import React, { useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis";

gsap.registerPlugin(ScrollTrigger);

export default function ViraGeometricReactor() {
  const wrapperRef = useRef(null);
  const sectionRef = useRef(null);

  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const panelsRef = useRef([]);
  const ringsRef = useRef([]);
  const starsRef = useRef([]);

  const titleRef = useRef(null);
  const subtitleRef = useRef(null);

  const lenisRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const OFFSET = 200;
  const STEP = 600;
  const NUM_PANELS = 24;
  const NUM_RINGS = 4;
  const NUM_STARS = 60;

  const scenes = [
    {
      title: "توسعه اکوسیستم‌های دیجیتال",
      subtitle: "طراحی و پیاده‌سازی زیرساخت‌های پایدار برای سازمان‌های پیشرو",
      // Darkest navy - The professional introduction
      color: "#2f257e",
    },
    {
      title: "مهندسی تجربه کاربری",
      subtitle: "خلق راهکارهای تعاملی مبتنی بر معماری اطلاعات و رفتار مخاطب",
      // Deep blue - UI/UX and logic
      color: "#274c83",
    },
    {
      title: "پویایی و عملکرد ساختاریافته",
      subtitle:
        "به‌کارگیری تکنولوژی‌های مدرن برای ارائه تجربه‌های بصری بدون افت پرفورمنس",
      // Teal - Performance and motion
      color: "#1c8e8c",
    },
    {
      title: "مقیاس‌پذیری و امنیت",
      subtitle:
        "توسعه هسته‌های پردازشی قدرتمند برای پاسخگویی به چالش‌های کلان سازمانی",
      // Bright green - The core backend/network resolution
      color: "#12c895",
    },
  ];

  const setPanelRef = (el, index) => {
    if (el) panelsRef.current[index] = el;
  };

  useEffect(() => {
    const isMobile = window.innerWidth < 900;

    if (!isMobile) {
      lenisRef.current = new Lenis({
        duration: 1.2,
        smoothWheel: true,
        smoothTouch: false,
      });

      lenisRef.current.on("scroll", ScrollTrigger.update);

      const updateLenis = (time) => {
        lenisRef.current?.raf(time * 1000);
      };

      gsap.ticker.add(updateLenis);
      gsap.ticker.lagSmoothing(0);
    }

    const ctx = gsap.context(() => {
      let current = -1;

      // انیمیشن شناور بودن لوگو در حالت اول
      if (logoRef.current) {
        gsap.to(logoRef.current, {
          y: -15,
          duration: 2.5,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      }

      ringsRef.current.forEach((ring, i) => {
        if (!ring) return;
        gsap.to(ring, {
          rotationX: i % 2 === 0 ? 360 : -360,
          rotationY: i % 3 === 0 ? 360 : -360,
          rotationZ: 360,
          duration: isMobile ? 40 + i * 10 : 30 + i * 5,
          repeat: -1,
          ease: "none",
        });
      });

      starsRef.current.forEach((star) => {
        if (!star) return;
        gsap.to(star, {
          z: isMobile ? 400 : 1000,
          opacity: 0,
          duration: "random(2, 5)",
          repeat: -1,
          ease: "none",
          delay: "random(0, 3)",
        });
      });

      gsap.set([titleRef.current, subtitleRef.current], {
        opacity: 0,
        y: 50,
        scale: 0.8,
      });

      const trigger = ScrollTrigger.create({
        trigger: wrapperRef.current,
        pin: sectionRef.current,
        pinSpacing: true,
        fastScrollEnd: true,
        start: "top top",
        end: () => `+=${OFFSET + scenes.length * STEP}`,
        onUpdate: (self) => {
          const scrollY = self.scroll() - self.start;
          let index = -1;

          if (scrollY >= OFFSET) {
            index = Math.floor((scrollY - OFFSET) / STEP);
            index = Math.min(scenes.length - 1, Math.max(0, index));
          }

          if (index !== current) {
            const isScrollingBack = self.direction === -1;
            current = index;
            if (index >= 0) animatePhase(index, isMobile, isScrollingBack);
            else resetReactor(isMobile);
          }
        },
      });

      function resetReactor(isMobileDevice) {
        clearTimeout(scrollTimeoutRef.current);

        // نمایش مجدد لوگو
        gsap.to(logoRef.current, {
          opacity: 1,
          scale: 1,
          z: 0,
          duration: 0.8,
          ease: "back.out(1.5)",
          overwrite: "auto",
        });

        gsap.to(containerRef.current, {
          z: 0,
          rotationX: 0,
          rotationY: 0,
          duration: 0.8,
          ease: "power3.out",
          overwrite: "auto",
        });

        gsap.to([titleRef.current, subtitleRef.current], {
          opacity: 0,
          y: 50,
          scale: 0.8,
          duration: 0.4,
          overwrite: "auto",
        });

        panelsRef.current.forEach((panel) => {
          if (!panel) return;
          gsap.to(panel, {
            x: () => (Math.random() - 0.5) * (isMobileDevice ? 300 : 800),
            y: () => (Math.random() - 0.5) * (isMobileDevice ? 400 : 800),
            z: () => (isMobileDevice ? 0 : (Math.random() - 0.5) * 800),
            rotationX: () => (isMobileDevice ? 0 : Math.random() * 360),
            rotationY: () => (isMobileDevice ? 0 : Math.random() * 360),
            rotationZ: () => Math.random() * 360,
            scale: isMobileDevice ? 0.7 : 0.5,
            borderColor: "rgba(47, 37, 126, 0.2)", // ته‌رنگ سورمه‌ای لوگو
            backgroundColor: "rgba(0,0,0,0)",
            duration: 0.8,
            ease: "power3.out",
            overwrite: "auto",
          });
        });
      }

      function animatePhase(phase, isMobileDevice, isScrollingBack) {
        const s = scenes[phase];
        const isLast = phase === scenes.length - 1;

        const baseDuration = isScrollingBack ? 0.4 : 1.2;
        const delayMultiplier = isScrollingBack ? 0 : 1;

        clearTimeout(scrollTimeoutRef.current);

        // محو کردن لوگو (با افکت رد شدن از داخل آن)
        gsap.to(logoRef.current, {
          opacity: 0,
          scale: isScrollingBack ? 0.5 : 3,
          z: 300,
          duration: baseDuration,
          ease: "power2.inOut",
          overwrite: "auto",
        });

        ringsRef.current.forEach((ring) => {
          if (!ring) return;
          gsap.to(ring, {
            borderColor: isLast ? s.color : `${s.color}40`,
            boxShadow: isLast
              ? `0 0 30px ${s.color}80, inset 0 0 30px ${s.color}80`
              : "none",
            duration: baseDuration,
            overwrite: "auto",
          });
        });

        if (isMobileDevice) {
          gsap.to(containerRef.current, {
            rotationX: phase === 2 ? 15 : 0,
            rotationY: phase === 1 ? -15 : 0,
            z: 0,
            duration: baseDuration,
            ease: "power2.out",
            overwrite: "auto",
          });

          panelsRef.current.forEach((panel, i) => {
            if (!panel) return;
            let config = {};
            const angle = (i / NUM_PANELS) * Math.PI * 2;

            if (phase === 0) {
              config = {
                x: Math.cos(angle) * 140,
                y: Math.sin(angle) * 140,
                z: 0,
                rotationZ: (angle * 180) / Math.PI + 90,
                scale: 0.85,
              };
            } else if (phase === 1) {
              const cols = 4;
              const col = i % cols;
              const row = Math.floor(i / cols);
              config = {
                x: (col - cols / 2 + 0.5) * 65,
                y: (row - 3 + 0.5) * 85,
                z: 0,
                rotationZ: 0,
                scale: 0.9,
              };
            } else if (phase === 2) {
              const factor = i % 2 === 0 ? 100 : 180;
              config = {
                x: Math.cos(angle) * factor,
                y: Math.sin(angle) * factor,
                z: 0,
                rotationZ: i * 20,
                scale: 0.75,
              };
            } else if (phase === 3) {
              config = { x: 0, y: 0, z: 0, rotationZ: i * 30, scale: 0.15 };
            }

            gsap.to(panel, {
              ...config,
              rotationX: 0,
              rotationY: 0,
              borderColor: s.color,
              backgroundColor: phase === 3 ? s.color : `${s.color}08`,
              duration: baseDuration,
              ease: "power2.out",
              delay: (i % 6) * 0.02 * delayMultiplier,
              overwrite: "auto",
            });
          });
        } else {
          // نسخه دسکتاپ
          if (phase === 0) {
            gsap.to(containerRef.current, {
              rotationX: -10,
              rotationY: 45,
              z: 0,
              duration: baseDuration,
              ease: "power3.inOut",
              overwrite: "auto",
            });
            panelsRef.current.forEach((panel, i) => {
              if (!panel) return;
              const angle = (i / NUM_PANELS) * Math.PI * 2;
              const radius = 250;
              const yOffset = (i % 3 === 0 ? -1 : i % 2 === 0 ? 0 : 1) * 80;

              gsap.to(panel, {
                x: Math.cos(angle) * radius,
                z: Math.sin(angle) * radius,
                y: yOffset,
                rotationY: (-angle * 180) / Math.PI + 90,
                rotationX: 0,
                rotationZ: 0,
                scale: 1,
                borderColor: s.color,
                backgroundColor: `${s.color}10`,
                duration: baseDuration,
                ease: "expo.out",
                delay: i * 0.02 * delayMultiplier,
                overwrite: "auto",
              });
            });
          } else if (phase === 1) {
            gsap.to(containerRef.current, {
              rotationX: 15,
              rotationY: -15,
              z: 150,
              duration: baseDuration,
              ease: "power3.inOut",
              overwrite: "auto",
            });
            panelsRef.current.forEach((panel, i) => {
              if (!panel) return;
              const cols = 6;
              const col = i % cols;
              const row = Math.floor(i / cols);
              const spacingX = 90;
              const spacingY = 140;

              gsap.to(panel, {
                x: (col - cols / 2 + 0.5) * spacingX,
                y: (row - 2 + 0.5) * spacingY,
                z: i % 2 === 0 ? 20 : -20,
                rotationY: 0,
                rotationX: 0,
                rotationZ: 0,
                scale: 1.2,
                borderColor: s.color,
                backgroundColor: `${s.color}20`,
                duration: baseDuration,
                ease: "back.out(1.2)",
                delay: (col + row) * 0.05 * delayMultiplier,
                overwrite: "auto",
              });
            });
          } else if (phase === 2) {
            gsap.to(containerRef.current, {
              rotationX: 45,
              rotationY: 90,
              z: -100,
              duration: baseDuration,
              ease: "power3.inOut",
              overwrite: "auto",
            });
            panelsRef.current.forEach((panel, i) => {
              if (!panel) return;
              const phi = Math.acos(-1 + (2 * i) / NUM_PANELS);
              const theta = Math.sqrt(NUM_PANELS * Math.PI) * phi;
              const radius = 300;

              gsap.to(panel, {
                x: radius * Math.cos(theta) * Math.sin(phi),
                y: radius * Math.sin(theta) * Math.sin(phi),
                z: radius * Math.cos(phi),
                rotationX: (theta * 180) / Math.PI,
                rotationY: (phi * 180) / Math.PI,
                rotationZ: i * 15,
                scale: 0.8,
                borderColor: s.color,
                backgroundColor: `${s.color}10`,
                duration: baseDuration,
                ease: "power4.out",
                delay: i * 0.01 * delayMultiplier,
                overwrite: "auto",
              });
            });
          } else if (phase === 3) {
            panelsRef.current.forEach((panel, i) => {
              if (!panel) return;
              gsap.to(panel, {
                x: 0,
                y: 0,
                z: 0,
                rotationX: 0,
                rotationY: 0,
                rotationZ: i * 15,
                scale: 0.1,
                borderColor: "transparent",
                backgroundColor: s.color,
                duration: baseDuration,
                ease: "expo.in",
                overwrite: "auto",
              });
            });

            gsap.to(containerRef.current, {
              z: 2500,
              rotationX: 0,
              rotationY: 0,
              duration: baseDuration,
              delay: 1.1 * delayMultiplier,
              ease: "power4.in",
              overwrite: "auto",
            });
          }
        }

        titleRef.current.textContent = s.title;
        subtitleRef.current.textContent = s.subtitle;

        gsap.fromTo(
          [titleRef.current, subtitleRef.current],
          { opacity: 0, y: isMobileDevice ? 25 : 40, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            scale: isLast ? (isMobileDevice ? 1.1 : 1.2) : 1,
            duration: 0.6,
            stagger: 0.1 * delayMultiplier,
            ease: "expo.out",
            overwrite: "auto",
          },
        );

        if (isLast && !isScrollingBack) {
          scrollTimeoutRef.current = setTimeout(() => {
            if (lenisRef.current) {
              lenisRef.current.scrollTo(window.scrollY + 600, {
                duration: 1.5,
                lock: false,
              });
            } else {
              window.scrollTo({
                top: window.scrollY + 600,
                behavior: "smooth",
              });
            }
          }, 2300);
        }
      }

      resetReactor(isMobile);

      return () => trigger.kill();
    }, wrapperRef);

    return () => {
      ctx.revert();
      if (lenisRef.current) {
        gsap.ticker.remove(lenisRef.current.raf);
        lenisRef.current.destroy();
      }
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  return (
    <Box
      ref={wrapperRef}
      sx={{ position: "relative", width: "100%", zIndex: 10 }}
    >
      <Box
        ref={sectionRef}
        sx={{
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          perspective: { xs: "none", md: "1200px" },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            transformStyle: { xs: "flat", md: "preserve-3d" },
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: NUM_STARS }).map((_, i) => (
            <Box
              key={`star-${i}`}
              ref={(el) => (starsRef.current[i] = el)}
              sx={{
                position: "absolute",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: "2px",
                backgroundColor: "rgba(255,255,255,0.3)",
                transform: {
                  xs: "none",
                  md: `translateZ(${-Math.random() * 1000}px)`,
                },
                willChange: "transform, opacity",
              }}
            />
          ))}
        </Box>

        {/* --- المان لوگوی ویرا --- */}
        <Box
          ref={logoRef}
          sx={{
            position: "absolute",
            width: { xs: "180px", md: "250px" },
            height: { xs: "180px", md: "250px" },
            zIndex: 5,
            willChange: "transform, opacity",
            filter: "drop-shadow(0px 0px 25px rgba(28, 142, 140, 0.3))",
          }}
        >
          <svg viewBox="0 0 500 500" width="100%" height="100%">
            <defs>
              <linearGradient
                id="logo-gradient"
                x1="201.25"
                y1="398.17"
                x2="377.67"
                y2="142.6"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#2f257e" />
                <stop offset=".21" stopColor="#274c83" />
                <stop offset=".59" stopColor="#1c8e8c" />
                <stop offset=".86" stopColor="#14b892" />
                <stop offset="1" stopColor="#12c895" />
              </linearGradient>
            </defs>
            <path
              d="m313.11,167.14c2.23,0,3.8,2.2,3.05,4.31l-108.01,305.08-6.53,17.86c-.58,1.57-1.72,2.82-3.15,3.55l-20.09.06-67.09.22-19.79.06c-.75-.5-1.13-1.48-.79-2.41l6.9-18.98,107.97-306.49c.61-1.75,2.26-2.92,4.11-2.92l16.05-.05c2.23,0,3.8,2.2,3.06,4.3l-106.62,303.07c-.34.98.38,2,1.42,2l60.28-.19c.64,0,1.2-.4,1.41-1l107.57-305.49c.61-1.75,2.26-2.92,4.11-2.92l16.14-.05Z"
              fill="#2f257e"
            />
            <path
              d="m374.11,7.79l-23.99,67.77c-.62,1.74-2.26,2.91-4.11,2.91l-16.32.05c-2.23,0-3.8-2.2-3.06-4.3l16.24-46.12c1.01-2.75-1.03-5.66-3.96-5.66l-54.61-.03c-1.78,0-3.36,1.11-3.96,2.77l-17.82,50.65c-.61,1.75-2.26,2.92-4.11,2.92l-16,.05c-2.23,0-3.8-2.2-3.06-4.3l24.23-68.79C264.84,2.28,268.11,0,271.76,0l96.93.04c4.01,0,6.8,3.99,5.42,7.76Z"
              fill="#2f257e"
            />
            <path
              d="m164.56,213.81l-22.48,63.21c-.74,1.99-3.43,2.29-4.59.5L18.88,96.79c-1.12-1.72.12-4,2.17-4h62.64c2.73,0,5.27,1.38,6.76,3.67l73.54,112.23c.99,1.52,1.2,3.42.56,5.11Z"
              fill="#2f257e"
            />
            <path
              d="m464.63,153.1s-103.36,321.63-111.4,345.29c-.28.82-1.05,1.38-1.92,1.38-8.72.04-54.89.19-65.13.23-1.29,0-2.49-.63-3.22-1.69-6.27-9.17-34.77-52.36-34.77-52.36-1.09-1.7-1.32-3.82-.62-5.72l23.07-64.13c.74-2.01,3.45-2.3,4.6-.51l29.43,45.62c.72,1.12,2.41.89,2.82-.37l84.83-262.84c.74-2.31-.98-4.67-3.4-4.66l-214.24.7c-2.5,0-4.28-2.43-3.51-4.8l16.71-51.76c.85-2.63,3.3-4.42,6.06-4.43l280.16-.91c5.04-.02,8.62,4.89,7.08,9.69l-16.55,51.26Z"
              fill="url(#logo-gradient)"
            />
          </svg>
        </Box>

        <Box
          ref={containerRef}
          sx={{
            position: "absolute",
            width: 0,
            height: 0,
            transformStyle: { xs: "flat", md: "preserve-3d" },
            willChange: "transform",
          }}
        >
          {Array.from({ length: NUM_RINGS }).map((_, i) => {
            const baseSize = 600 + i * 150;
            return (
              <Box
                key={`ring-${i}`}
                ref={(el) => (ringsRef.current[i] = el)}
                sx={{
                  position: "absolute",
                  left: -baseSize / 2,
                  top: -baseSize / 2,
                  width: baseSize,
                  height: baseSize,
                  "@media (max-width:900px)": {
                    width: 240 + i * 60,
                    height: 240 + i * 60,
                    left: -(240 + i * 60) / 2,
                    top: -(240 + i * 60) / 2,
                  },
                  borderRadius: "50%",
                  border: "1.5px solid rgba(255,255,255,0.03)",
                  borderStyle: i % 2 === 0 ? "dashed" : "solid",
                  transformStyle: { xs: "flat", md: "preserve-3d" },
                  willChange: "transform, border-color",
                }}
              />
            );
          })}

          {Array.from({ length: NUM_PANELS }).map((_, i) => (
            <Box
              key={`panel-${i}`}
              ref={(el) => setPanelRef(el, i)}
              sx={{
                position: "absolute",
                left: { xs: -22, md: -40 },
                top: { xs: -35, md: -60 },
                width: { xs: 44, md: 80 },
                height: { xs: 70, md: 120 },
                border: "1.5px solid rgba(255, 255, 255, 0.08)",
                borderRadius: { xs: "4px", md: "8px" },
                backgroundColor: "transparent",
                transformStyle: { xs: "flat", md: "preserve-3d" },
                willChange: "transform, border-color, background-color",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "25%",
                  height: "1px",
                  bgcolor: "rgba(255,255,255,0.25)",
                }}
              />
            </Box>
          ))}
        </Box>

        <Stack
          sx={{
            position: "relative",
            textAlign: "center",
            zIndex: 10,
            width: "100%",
            maxWidth: { xs: 340, md: 600 },
            px: 2.5,
            transform: { xs: "none", md: "translateZ(300px)" },
            pointerEvents: "none",
          }}
        >
          <Typography
            ref={titleRef}
            sx={{
              fontSize: { xs: 28, md: 32 },
              fontWeight: 900,
              mb: 1.5,
              letterSpacing: { xs: "-1px", md: "-2px" },
              textShadow: "0px 10px 30px rgba(0,0,0,1)",
              textTransform: "uppercase",
              color: "#fff",
              direction: "rtl",
              willChange: "transform, opacity",
            }}
          />
          <Typography
            ref={subtitleRef}
            sx={{
              opacity: 0.8,
              fontSize: { xs: 14, md: 16 },
              fontWeight: 400,
              letterSpacing: { xs: "1px", md: "3px" },
              textShadow: "0px 5px 15px rgba(0,0,0,1)",
              color: "rgba(255,255,255,0.7)",
              direction: "rtl",
              willChange: "transform, opacity",
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
